import type { DataTableFilterField } from '@neo/blocks/data-table/types'
import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { Button } from '@neo/index'
import { TruncatedTextWithTooltip } from '@neo/primitives/truncated-text-with-tooltip'
import type { ColumnDef } from '@tanstack/react-table'
import { MoreVertical } from 'lucide-react'
import type { SecretVariable } from 'v2/pages/flow/sidebar/types'
import dayjs from 'v2/utils/dayjs'
import z from 'zod'

export interface SecretVariableForm extends Pick<SecretVariable, 'name' | 'value' | 'description'> {
  id?: string
}

export const columns: ColumnDef<SecretVariable>[] = [
  {
    header: 'Key',
    accessorKey: 'name',
    cell: ({ row }) => (
      <TruncatedTextWithTooltip
        className="min-w-40 font-jetbrains text-xs-400"
        tooltipContent={row.original.name}
      >
        {row.original.name}
      </TruncatedTextWithTooltip>
    ),
  },
  {
    header: 'Description',
    accessorKey: 'description',
    cell: ({ row }) => (
      <TruncatedTextWithTooltip
        className="min-w-40 text-s-400"
        tooltipContent={row.original.description}
      >
        {row.original.description}
      </TruncatedTextWithTooltip>
    ),
  },
  {
    header: 'Created by',
    accessorKey: 'created_by.name',
    cell: ({ row }) => <div className="text-s-400 capitalize">{row.original.created_by.name}</div>,
  },
  {
    header: 'Updated at',
    accessorKey: 'updated_at',
    cell: ({ row }) => (
      <span className="font-jetbrains text-xs-400 tabular-nums">
        {dayjs(row.original.updated_at).format('D MMMM YYYY, h:mma')}
      </span>
    ),
  },
  {
    header: '',
    accessorKey: 'actions',
    cell: () => (
      <div className="flex items-center gap-2">
        <Button
          size="iconSm"
          variant="ghost"
          className="h-7 w-7 p-0 text-secondary opacity-0 group-hover:opacity-100 focus-visible:opacity-100 [.context-menu-open_&]:opacity-100" // Note to future dev: This is to ensure the menu is visible when the context menu is open
          onClick={(e) => {
            e.stopPropagation()
            const rect = e.currentTarget.getBoundingClientRect()
            const customEvent = new MouseEvent('contextmenu', {
              bubbles: true,
              cancelable: true,
              clientX: rect.left + 30,
              clientY: rect.bottom,
            })
            e.currentTarget.dispatchEvent(customEvent)
          }}
        >
          <MoreVertical size={16} />
          <span className="sr-only">Open menu</span>
        </Button>
      </div>
    ),
  },
]

export const filterFields: DataTableFilterField<SecretVariable>[] = [
  { label: 'Name', value: 'name', paramKey: 'name', placeholder: 'Search by name...' },
]

export const formFields: GenericFieldConfig<SecretVariableForm> = [
  {
    name: 'name',
    label: 'Key',
    componentType: 'input',
    zodValidation: z
      .string()
      .min(1, 'Key is required')
      .max(255, 'Key must be less than 255 characters'),
    inputProps: {
      placeholder: 'Enter secret key',
      autoFocus: true,
    },
    eventDataExtractor: (e) =>
      z
        .string()
        .transform((str) => str.toUpperCase().replace(/[^A-Z0-9]/g, '_'))
        .parse(e.target?.value),
  },
  {
    name: 'value',
    label: 'Value',
    componentType: 'inputPassword',
    zodValidation: z.string().min(1, 'Value is required'),
    inputProps: {
      placeholder: 'Enter secret value',
    },
  },
  {
    name: 'description',
    label: 'Description',
    componentType: 'textarea',
    zodValidation: z.string().min(1, 'Description is required'),
    inputProps: {
      placeholder: 'Enter a short description of the secret',
    },
  },
]
